import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ChevronDown, BarChart, Clock, ExternalLink, RefreshCw, Globe, ChevronRight, ChevronLeft, AlertTriangle, Check, PlusCircle, Info, Settings, X } from "lucide-react";
import pageService from "../../services/pageService";
import websiteService from "../../services/websiteService";
import websitePageSpeed from "../../services/WebsitePageSpeedService";
import PageSpeedAnalysisModal from "./components/PageSpeedAnalysisModal";

// Component for page row with analysis status
const PageRow = ({ page, formatDate, getStatusColor, getStatusTextColor, handleViewDetails, handleReanalyze, handlePageSettings }) => {
  const [analysisStatus, setAnalysisStatus] = useState({
    hasDesktopScore: false,
    hasMobileScore: false,
    hasBeenAnalyzed: false,
    pageSpeedData: null,
    loading: true
  });
  
  useEffect(() => {
    const getStatus = async () => {
      try {
        const status = await pageService.getAnalysisStatus(page.id);
        setAnalysisStatus({
          ...status,
          loading: false
        });
      } catch (error) {
        console.error(`Error fetching analysis status for page ${page.id}:`, error);
        setAnalysisStatus(prev => ({
          ...prev,
          loading: false
        }));
      }
    };
    
    getStatus();
  }, [page.id]);
  
  const { hasDesktopScore, hasMobileScore, hasBeenAnalyzed, pageSpeedData, loading } = analysisStatus;
  const desktopScore = pageSpeedData?.performance_score;
  const mobileScore = pageSpeedData?.mobile_performance_score;
  const lastAnalyzedDate = pageSpeedData?.updated_at || page.updated_at;
  
  if (loading) {
    return (
      <tr className="bg-white border-b hover:bg-gray-50">
        <td colSpan="5" className="px-6 py-4 text-center">
          <div className="flex justify-center items-center">
            <div className="w-5 h-5 border-t-2 border-blue-500 rounded-full animate-spin"></div>
            <span className="ml-2">Loading analysis data...</span>
          </div>
        </td>
      </tr>
    );
  }
  
  return (
    <tr key={page.id} className="bg-white border-b hover:bg-gray-50">
      <td className="px-6 py-4">
        <div>
          <Link 
            to={`/dashboard/pagespeed/${page.id}`}
            className="font-medium text-blue-600 hover:underline"
          >
            {page.url}
          </Link>
          <p className="text-xs text-gray-500 mt-1">
            {page.title || "No title"}
          </p>
        </div>
      </td>
      <td className="px-6 py-4">
        {hasDesktopScore ? (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              <div className="h-2 w-20 rounded-full bg-gray-200">
                <div
                  className={`h-2 rounded-full ${getStatusColor(desktopScore)}`}
                  style={{ width: `${desktopScore * 100 || 0}%` }}
                ></div>
              </div>
              <span className={`font-medium ${getStatusTextColor(desktopScore)}`}>
                {Math.round(desktopScore * 100)}
              </span>
            </div>
            <div className="text-xs text-gray-500">Desktop Score</div>
          </div>
        ) : (
          <span className="text-gray-500">Not analyzed</span>
        )}
      </td>
      <td className="px-6 py-4">
        {hasMobileScore ? (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              <div className="h-2 w-20 rounded-full bg-gray-200">
                <div
                  className={`h-2 rounded-full ${getStatusColor(mobileScore)}`}
                  style={{ width: `${mobileScore * 100 || 0}%` }}
                ></div>
              </div>
              <span className={`font-medium ${getStatusTextColor(mobileScore)}`}>
                {Math.round(mobileScore * 100)}
              </span>
            </div>
            <div className="text-xs text-gray-500">Mobile Score</div>
          </div>
        ) : (
          <span className="text-gray-500">Not analyzed</span>
        )}
      </td>
      <td className="px-6 py-4 text-gray-500">
        {hasBeenAnalyzed ? (
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1.5" />
            {formatDate(lastAnalyzedDate)}
          </div>
        ) : (
          <div className="flex items-center">
            <span className="text-gray-500 text-sm mr-2">Not analyzed yet</span>
            <button 
              onClick={() => handleReanalyze(page)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Run analysis
            </button>
          </div>
        )}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button
            className="text-gray-500 hover:text-blue-600"
            title="View details"
            onClick={() => handleViewDetails(page)}
          >
            <BarChart className="h-4 w-4" />
          </button>
          <button
            className="text-gray-500 hover:text-green-600"
            title="Re-analyze"
            onClick={() => handleReanalyze(page)}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <a
            href={page.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-blue-600"
            title="Visit page"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            className="text-gray-500 hover:text-gray-700"
            title="More options"
            onClick={() => handlePageSettings(page)}
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// New Analysis Modal Component
const NewAnalysisModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!url) {
      setError('Please enter a URL');
      return;
    }
    
    try {
      const urlObj = new URL(url);
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        setError('URL must start with http:// or https://');
        return;
      }
      setError('');
      onSubmit(url);
    } catch (e) {
      setError('Please enter a valid URL');
    }
  };
  
  const handleClose = () => {
    setUrl('');
    setError('');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">New PageSpeed Analysis</h3>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                  Page URL
                </label>
                <input
                  type="text"
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/page"
                  className={`mt-1 block w-full rounded-md border ${
                    error ? 'border-red-300' : 'border-gray-300'
                  } shadow-sm py-2 px-3 focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50`}
                />
                {error && (
                  <p className="mt-2 text-sm text-red-600">{error}</p>
                )}
              </div>
              
              <div>
                <p className="text-sm text-gray-500">
                  Enter the URL of the page you want to analyze. The analysis will check both mobile and desktop versions.
                </p>
              </div>
            </div>
          </div>
          
          <div className="px-6 py-4 bg-gray-50 text-right rounded-b-lg">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin"></div>
                  Analyzing...
                </>
              ) : (
                'Start Analysis'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PageSpeed = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [websiteFilter, setWebsiteFilter] = useState("");
  const [performanceFilter, setPerformanceFilter] = useState("");
  const [timeFilter, setTimeFilter] = useState("");
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedWebsites, setExpandedWebsites] = useState({});
  const [websitePages, setWebsitePages] = useState({});
  const [websitePagination, setWebsitePagination] = useState({});
  const [loadingWebsites, setLoadingWebsites] = useState({});
  const [toast, setToast] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showNewAnalysisModal, setShowNewAnalysisModal] = useState(false);
  const [pageSpeedAnalysisState, setPageSpeedAnalysisState] = useState({
    isRunning: false,
    progress: 0,
    status: 'starting',
    message: 'Initializing PageSpeed analysis...'
  });

  const fetchWebsitePages = useCallback(async (websiteId, options = {}) => {
    if (websiteFilter && websiteId !== websiteFilter) return;
    setLoadingWebsites(prev => ({ ...prev, [websiteId]: true }));
    try {
      const paginationForWebsite = websitePagination[websiteId] || { limit: 10, offset: 0 };
      const limit = options.limit || paginationForWebsite.limit;
      const offset = options.offset !== undefined ? options.offset : paginationForWebsite.offset;
      const response = await pageService.getWebsitePages(websiteId, { limit, offset });
      if (response.success) {
        setWebsitePages(prev => ({ ...prev, [websiteId]: response.pages || [] }));
        setWebsitePagination(prev => ({
          ...prev,
          [websiteId]: {
            total: response.total || 0,
            limit: response.limit || limit,
            offset: response.offset || offset
          }
        }));
      }
    } catch (err) {
      console.error(`Error fetching pages for website ${websiteId}:`, err);
    } finally {
      setLoadingWebsites(prev => ({ ...prev, [websiteId]: false }));
    }
  }, [websiteFilter, websitePagination]);

  const fetchWebsites = useCallback(async () => {
    try {
      setLoading(true);
      const websitesResponse = await websiteService.getAllWebsites();
      if (websitesResponse.success && websitesResponse.websites) {
        const websitesList = websitesResponse.websites;
        setWebsites(websitesList);
        if (websitesList.length > 0 && Object.keys(expandedWebsites).length === 0) {
          setExpandedWebsites({ [websitesList[0].id]: true });
          fetchWebsitePages(websitesList[0].id);
        }
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching websites:", err);
      setError("Failed to load websites. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [expandedWebsites, fetchWebsitePages]);

  useEffect(() => {
    fetchWebsites();
  }, [fetchWebsites]);

  useEffect(() => {
    Object.keys(expandedWebsites).forEach(websiteId => {
      if (expandedWebsites[websiteId] && (!websitePages[websiteId] || websitePages[websiteId].length === 0)) {
        fetchWebsitePages(websiteId);
      }
    });
  }, [expandedWebsites, websitePages, fetchWebsitePages]);

  useEffect(() => {
    if (websiteFilter) {
      fetchWebsitePages(websiteFilter);
    }
  }, [websiteFilter, fetchWebsitePages]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filterWebsitesAndPages = () => {
    return websites.filter(website => {
      if (websiteFilter && website.id !== websiteFilter) return false;
      const pages = websitePages[website.id] || [];
      const websiteMatches = !searchQuery || 
        website.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        website.root_url?.toLowerCase().includes(searchQuery.toLowerCase());
      if (websiteMatches) return true;
      const filteredPages = pages.filter(page => 
        page.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (page.title && page.title.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      if (performanceFilter) {
        const minScore = performanceFilter === 'good' ? 90 :
                       performanceFilter === 'average' ? 70 : 0;
        const maxScore = performanceFilter === 'good' ? 100 :
                       performanceFilter === 'average' ? 89 : 69;
        return filteredPages.some(page => {
          const score = page.performance_score || 0;
          return score >= minScore && score <= maxScore;
        });
      }
      return filteredPages.length > 0;
    });
  };

  const getFilteredPagesForWebsite = (websiteId) => {
    const pages = websitePages[websiteId] || [];
    return pages.filter(page => {
      const matchesSearch = !searchQuery || 
        page.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (page.title && page.title.toLowerCase().includes(searchQuery.toLowerCase()));
      if (!matchesSearch) return false;
      if (performanceFilter) {
        const score = page.performance_score || 0;
        if (performanceFilter === 'good' && score < 90) return false;
        if (performanceFilter === 'average' && (score < 70 || score >= 90)) return false;
        if (performanceFilter === 'poor' && score >= 70) return false;
      }
      return true;
    });
  };

  const toggleWebsite = (websiteId) => {
    setExpandedWebsites(prev => ({
      ...prev,
      [websiteId]: !prev[websiteId]
    }));
  };

  const applyFilters = () => {
    if (websiteFilter) {
      fetchWebsitePages(websiteFilter, { offset: 0 });
    }
  };

  const clearFilters = () => {
    setWebsiteFilter("");
    setPerformanceFilter("");
    setTimeFilter("");
    setSearchQuery("");
    if (websites.length > 0) {
      fetchWebsitePages(websites[0].id, { offset: 0 });
    }
  };

  const handleWebsiteNextPage = (websiteId) => {
    const pagination = websitePagination[websiteId];
    if (!pagination) return;
    if (pagination.offset + pagination.limit < pagination.total) {
      fetchWebsitePages(websiteId, {
        offset: pagination.offset + pagination.limit,
        limit: pagination.limit
      });
    }
  };

  const handleWebsitePrevPage = (websiteId) => {
    const pagination = websitePagination[websiteId];
    if (!pagination) return;
    if (pagination.offset > 0) {
      fetchWebsitePages(websiteId, {
        offset: Math.max(0, pagination.offset - pagination.limit),
        limit: pagination.limit
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMins > 0) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    } else {
      return "Just now";
    }
  };

  const handleReanalyze = async (page) => {
    try {
      if (!page.website_id) {
        setToast({
          visible: true,
          message: "Page must be associated with a website to analyze",
          type: "error"
        });
        return;
      }
      const website = websites.find(w => w.id === page.website_id);
      if (!website) {
        setToast({
          visible: true,
          message: "Associated website not found",
          type: "error"
        });
        return;
      }
      setPageSpeedAnalysisState({
        isRunning: true,
        progress: 0,
        status: 'starting',
        message: 'Initializing PageSpeed analysis...'
      });
      const websiteObj = {
        id: website.id,
        root_url: page.url
      };
      await websitePageSpeed.analyzeWebsite(websiteObj, {
        desktop: true,
        mobile: true,
        onProgress: (progress) => {
          setPageSpeedAnalysisState({
            isRunning: progress.status !== 'complete' && progress.status !== 'error',
            progress: progress.progress,
            status: progress.status,
            message: progress.message
          });
        }
      });
      setToast({
        visible: true,
        message: "Page successfully analyzed!",
        type: "success"
      });
      const pagination = websitePagination[page.website_id] || { limit: 10, offset: 0 };
      fetchWebsitePages(page.website_id, {
        limit: pagination.limit,
        offset: pagination.offset
      });
    } catch (err) {
      console.error("Error reanalyzing page:", err);
      setToast({
        visible: true,
        message: err.message || "Failed to reanalyze page. Please try again.",
        type: "error"
      });
      setPageSpeedAnalysisState({
        isRunning: false,
        progress: 0,
        status: 'error',
        message: 'Analysis failed'
      });
    }
  };

  const getStatusColor = (score) => {
    if (!score && score !== 0) return "bg-gray-200";
    if (score >= 90) return "bg-green-500";
    if (score >= 70) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getStatusTextColor = (score) => {
    if (!score && score !== 0) return "text-gray-500";
    if (score >= 90) return "text-green-700";
    if (score >= 70) return "text-yellow-700";
    return "text-red-700";
  };

  const calculateWebsiteAverage = (pages, scoreType) => {
    if (!pages || pages.length === 0) return null;
    const validScores = pages
      .map(page => page[scoreType])
      .filter(score => score !== null && score !== undefined);
    if (validScores.length === 0) return null;
    const sum = validScores.reduce((acc, score) => acc + score, 0);
    return sum / validScores.length;
  };

  const handleViewDetails = (page) => {
    navigate(`/dashboard/pagespeed/${page.id}`);
  };

  const handlePageSettings = (page) => {
    setToast({
      visible: true,
      message: "Page settings will be implemented soon",
      type: "info"
    });
  };

  const closeToast = () => {
    setToast(null);
  };

  const handleAnalyzeUrl = async (url) => {
    setAnalysisLoading(true);
    setToast({
      visible: true,
      type: 'info',
      message: 'Starting analysis for ' + url
    });
    try {
      setPageSpeedAnalysisState({
        isRunning: true,
        progress: 0,
        status: 'starting',
        message: 'Initializing PageSpeed analysis...'
      });
      const websiteObj = {
        id: 'unknown',
        root_url: url
      };
      await websitePageSpeed.analyzeWebsite(websiteObj, {
        desktop: true,
        mobile: true,
        onProgress: (progress) => {
          setPageSpeedAnalysisState({
            isRunning: progress.status !== 'complete' && progress.status !== 'error',
            progress: progress.progress,
            status: progress.status,
            message: progress.message
          });
          setToast({
            visible: true,
            type: 'info',
            message: `Analyzing: ${progress.progress}% - ${progress.message}`
          });
        }
      });
      setToast({
        visible: true,
        type: 'success',
        message: 'Analysis completed successfully!'
      });
      fetchWebsites();
      setShowNewAnalysisModal(false);
    } catch (error) {
      console.error('Analysis failed:', error);
      setToast({
        visible: true,
        type: 'error',
        message: `Analysis failed: ${error.message || 'Unknown error'}`
      });
      setPageSpeedAnalysisState({
        isRunning: false,
        progress: 0,
        status: 'error',
        message: error.message || 'Unknown error'
      });
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
    <div className="container p-4 mx-auto">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">PageSpeed Analysis</h1>
          <p className="text-gray-500">Analyze and monitor your pages' performance</p>
        </div>
        <div className="mt-4 md:mt-0">
          <button 
            onClick={() => setShowNewAnalysisModal(true)}
            className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <PlusCircle className="h-4 w-4" />
            New Analysis
          </button>
        </div>
      </div>
      
      {toast && toast.visible && (
        <div className={`mb-4 p-3 rounded-md border flex items-center justify-between ${
          toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 
          toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 
          'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          <div className="flex items-center">
            {toast.type === 'success' && <Check className="h-4 w-4 mr-2" />}
            {toast.type === 'error' && <AlertTriangle className="h-4 w-4 mr-2" />}
            {toast.type === 'info' && <Info className="h-4 w-4 mr-2" />}
            <span>{toast.message}</span>
          </div>
          <button 
            onClick={closeToast}
            className="text-gray-500 hover:text-gray-700 ml-3"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
              placeholder="Search by URL or title..."
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>

          <button
            className="flex items-center px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
            <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${showFilters ? 'transform rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Performance</label>
              <select 
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={performanceFilter}
                onChange={(e) => setPerformanceFilter(e.target.value)}
              >
                <option value="">All performance levels</option>
                <option value="good">Good (90-100)</option>
                <option value="average">Average (70-89)</option>
                <option value="poor">Poor (0-69)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <select 
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={websiteFilter}
                onChange={(e) => setWebsiteFilter(e.target.value)}
              >
                <option value="">All websites</option>
                {websites.map(website => (
                  <option key={website.id} value={website.id}>
                    {website.name || website.root_url}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last analyzed</label>
              <select 
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
              >
                <option value="">Any time</option>
                <option value="today">Today</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
              </select>
            </div>
            <div className="md:col-span-3 flex justify-end">
              <button 
                className="text-sm text-gray-500 hover:text-gray-700 mr-4"
                onClick={clearFilters}
              >
                Clear filters
              </button>
              <button 
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                onClick={applyFilters}
              >
                Apply filters
              </button>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center items-center p-8">
          <div className="w-8 h-8 border-t-2 border-blue-600 border-r-2 rounded-full animate-spin"></div>
          <span className="ml-2 text-gray-600">Loading data...</span>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-700 mb-6">
          <p>{error}</p>
          <button 
            className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-6">
          {filterWebsitesAndPages().length > 0 ? (
            filterWebsitesAndPages().map((website) => {
              const websiteId = website.id;
              const isExpanded = expandedWebsites[websiteId];
              const pages = getFilteredPagesForWebsite(websiteId);
              const isLoading = loadingWebsites[websiteId];
              
              return (
                <div 
                  key={websiteId} 
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  <div 
                    className="bg-gray-50 px-6 py-4 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleWebsite(websiteId)}
                  >
                    <div className="flex items-center space-x-3">
                      <Globe className="h-5 w-5 text-gray-500" />
                      <h3 className="font-medium text-gray-800">{website.name || website.root_url}</h3>
                      <span className="text-sm text-gray-500">
                        ({websitePagination[websiteId]?.total || "0"} {websitePagination[websiteId]?.total === 1 ? 'page' : 'pages'})
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="hidden md:flex items-center gap-2">
                        <span className="text-xs text-gray-500">Desktop:</span>
                        <div className="h-2 w-16 rounded-full bg-gray-200">
                          <div
                            className={`h-2 rounded-full ${getStatusColor(calculateWebsiteAverage(pages, 'performance_score'))}`}
                            style={{ width: `${calculateWebsiteAverage(pages, 'performance_score') || 0}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs font-medium ${getStatusTextColor(calculateWebsiteAverage(pages, 'performance_score'))}`}>
                          {calculateWebsiteAverage(pages, 'performance_score') !== null
                            ? Math.round(calculateWebsiteAverage(pages, 'performance_score') * 100)
                            : "N/A"}
                        </span>
                      </div>
                      
                      <div className="hidden md:flex items-center gap-2">
                        <span className="text-xs text-gray-500">Mobile:</span>
                        <div className="h-2 w-16 rounded-full bg-gray-200">
                          <div
                            className={`h-2 rounded-full ${getStatusColor(calculateWebsiteAverage(pages, 'mobile_performance_score'))}`}
                            style={{ width: `${calculateWebsiteAverage(pages, 'mobile_performance_score') || 0}%` }}
                          ></div>
                        </div>
                        <span className={`text-xs font-medium ${getStatusTextColor(calculateWebsiteAverage(pages, 'mobile_performance_score'))}`}>
                          {calculateWebsiteAverage(pages, 'mobile_performance_score') !== null
                            ? Math.round(calculateWebsiteAverage(pages, 'mobile_performance_score') * 100)
                            : "N/A"}
                        </span>
                      </div>
                      
                      <ChevronRight 
                        className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-90' : ''}`} 
                      />
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div>
                      {isLoading && (
                        <div className="flex justify-center items-center p-6">
                          <div className="w-6 h-6 border-t-2 border-blue-600 border-r-2 rounded-full animate-spin"></div>
                          <span className="ml-2 text-gray-600">Loading pages...</span>
                        </div>
                      )}
                      
                      {!isLoading && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-left text-gray-700">
                            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-t border-gray-200">
                              <tr>
                                <th scope="col" className="px-6 py-3">Page URL</th>
                                <th scope="col" className="px-6 py-3">Desktop Score</th>
                                <th scope="col" className="px-6 py-3">Mobile Score</th>
                                <th scope="col" className="px-6 py-3">Last Analyzed</th>
                                <th scope="col" className="px-6 py-3">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pages.length > 0 ? (
                                pages.map((page) => (
                                  <PageRow 
                                    key={page.id}
                                    page={page}
                                    formatDate={formatDate}
                                    getStatusColor={getStatusColor}
                                    getStatusTextColor={getStatusTextColor}
                                    handleViewDetails={handleViewDetails}
                                    handleReanalyze={handleReanalyze}
                                    handlePageSettings={handlePageSettings}
                                  />
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                    <div className="flex flex-col items-center">
                                      <Search className="h-10 w-10 text-gray-400 mb-2" />
                                      <p className="text-lg font-medium">No pages found</p>
                                      <p className="text-sm">Try adjusting your search or filters</p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                          
                          {websitePagination[websiteId] && websitePagination[websiteId].total > websitePagination[websiteId].limit && (
                            <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                              <div className="flex items-center">
                                <p className="text-sm text-gray-700">
                                  Showing <span className="font-medium">{websitePagination[websiteId].offset + 1}</span> to{' '}
                                  <span className="font-medium">
                                    {Math.min(
                                      websitePagination[websiteId].offset + websitePagination[websiteId].limit,
                                      websitePagination[websiteId].total
                                    )}
                                  </span>{' '}
                                  of <span className="font-medium">{websitePagination[websiteId].total}</span> pages
                                </p>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleWebsitePrevPage(websiteId)}
                                  disabled={websitePagination[websiteId].offset === 0}
                                  className={`p-2 rounded-md ${
                                    websitePagination[websiteId].offset === 0
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  <ChevronLeft className="h-5 w-5" />
                                </button>
                                
                                <button
                                  onClick={() => handleWebsiteNextPage(websiteId)}
                                  disabled={
                                    websitePagination[websiteId].offset + websitePagination[websiteId].limit >=
                                    websitePagination[websiteId].total
                                  }
                                  className={`p-2 rounded-md ${
                                    websitePagination[websiteId].offset + websitePagination[websiteId].limit >=
                                    websitePagination[websiteId].total
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  <ChevronRight className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
              <div className="flex flex-col items-center">
                <Search className="h-10 w-10 text-gray-400 mb-2" />
                <p className="text-lg font-medium">No results found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            </div>
          )}
        </div>
      )}

      <PageSpeedAnalysisModal
        isOpen={pageSpeedAnalysisState.isRunning}
        progress={pageSpeedAnalysisState.progress}
        status={pageSpeedAnalysisState.status}
        message={pageSpeedAnalysisState.message}
      />

      <NewAnalysisModal
        isOpen={showNewAnalysisModal}
        onClose={() => setShowNewAnalysisModal(false)}
        onSubmit={handleAnalyzeUrl}
        loading={analysisLoading}
      />
    </div>
  );
};

export default PageSpeed;